/**
 * Panel **CONDONADOS** (pedido de Yago, 2026-08-10: *"un dashboard de
 * condonados y que de ahí se pueda volver a la lista de impagados, por si fue
 * un error"*).
 *
 * Qué se perdonó, **quién** lo decidió, cuándo y por qué — leído de la traza
 * propia de ms-ops (`write_offs`), no del ledger ajeno: ms-payment firma hoy
 * sus hechos con nuestro usuario de servicio, así que la persona solo vive
 * aquí (y dentro del motivo que le mandamos). Pedido R13 para que su ledger
 * firme con la persona también.
 *
 * ## Sobre «Restituir»
 *
 * Una condonación NO se deshace: se corrige. El botón lanza un ajuste positivo
 * en el ledger, así que el saldo vuelve a ser exigible y en la historia del
 * recibo quedan LOS DOS hechos (condonado el X por A, restituido el Y por B).
 * Es lo correcto en un libro de asientos y además es mejor para Ops: el error
 * queda visible y explicado, no desaparecido.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { listWriteOffs, restoreWriteOff } from "../../api/msops";
import type { WriteOffListItem } from "../../types/msops";
import RecobrosTabs from "./components/RecobrosTabs";
import { Card, COLORS as UI, ErrorBanner, Loading } from "./components/ui";

// Misma paleta que el resto del panel (ver nota en Cartera.tsx).
const COLORS = {
  heading: UI.heading,
  ink: UI.ink,
  muted: UI.muted,
  border: UI.border,
  link: UI.link,
};

const MARKET = "FR";

function eur(cents: number): string {
  return (cents / 100).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fecha(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function Condonados() {
  const { token } = useAuth();
  const [items, setItems] = useState<WriteOffListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<WriteOffListItem | null>(null);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await listWriteOffs(MARKET, { token });
      setItems(res.items);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const doRestore = useCallback(async () => {
    if (!restoring || motivo.trim().length < 5) return;
    setBusy(true);
    try {
      const res = await restoreWriteOff(restoring.id, motivo.trim(), { token });
      setNotice(
        `Restituidos ${res.receiptsRestored} recibo(s) · ${eur(res.centsRestored)} €. ` +
          "El cliente vuelve a la cartera de impagados y puede entrar en cadencia; la condonación " +
          "y su corrección quedan ambas en la historia." +
          (res.receiptsFailed.length > 0
            ? ` ⚠ ${res.receiptsFailed.length} recibo(s) los rechazó ms-payment: avisa a tecnología.`
            : ""),
      );
      setRestoring(null);
      setMotivo("");
      await load();
    } catch (err) {
      window.alert(
        `No se pudo restituir: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setBusy(false);
    }
  }, [restoring, motivo, token, load]);

  const total = (items ?? []).reduce((s, i) => s + (i.restoredAt ? 0 : i.centsWrittenOff), 0);

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
      <RecobrosTabs />
      <div style={{ display: "flex", alignItems: "baseline", gap: ".8rem", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: "1.15rem", color: COLORS.heading }}>Deuda perdonada · {MARKET}</h2>
        <Link to="/operations/recobros/cartera" style={{ color: COLORS.link, fontSize: ".85rem" }}>
          ← Volver a la deuda por cliente
        </Link>
      </div>
      <p style={{ color: COLORS.muted, fontSize: ".85rem", marginTop: ".3rem" }}>
        Deuda perdonada desde el panel, con quién lo decidió y por qué.{" "}
        {items && `${items.filter((i) => !i.restoredAt).length} vigentes · ${eur(total)} €`}
      </p>

      {error && <p style={{ color: "#721c24" }}>{error}</p>}
      {notice && (
        <div
          style={{
            padding: ".55rem .8rem",
            background: "#d4edda",
            color: "#155724",
            borderRadius: ".45rem",
            margin: "0 0 1rem",
            fontSize: ".85rem",
          }}
        >
          {notice}
        </div>
      )}

      {items && items.length === 0 && (
        <p style={{ color: COLORS.muted, fontSize: ".85rem" }}>
          Todavía no se ha condonado nada desde el panel. (Las 33 condonaciones del stock
          antiguo del 10/08 se hicieron con un barrido versionado, antes de que existiera
          esta traza — están en el ledger de ms-payment.)
        </p>
      )}

      {items && items.length > 0 && (
        <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: ".5rem" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900, fontSize: ".85rem" }}>
            <thead>
              <tr>
                {["Cuándo", "Cliente", "Importe", "Recibos", "Quién", "Motivo", ""].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: h === "Importe" ? "right" : "left",
                      fontSize: ".72rem",
                      textTransform: "uppercase",
                      letterSpacing: ".04em",
                      color: COLORS.muted,
                      background: "#f8f9fa",
                      padding: ".5rem .65rem",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} style={{ opacity: i.restoredAt ? 0.6 : 1 }}>
                  <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, whiteSpace: "nowrap" }}>
                    {fecha(i.createdAt)}
                  </td>
                  <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}` }}>
                    <Link
                      to={`/leads/${i.leadUlid}`}
                      style={{ color: COLORS.link, fontFamily: "monospace", fontSize: ".78rem" }}
                    >
                      {i.leadUlid.slice(0, 8)}…
                    </Link>
                  </td>
                  <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {eur(i.centsWrittenOff)} €
                  </td>
                  <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}` }}>
                    {i.receiptsWrittenOff}
                  </td>
                  <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, whiteSpace: "nowrap" }}>
                    {i.actor}
                  </td>
                  <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, color: COLORS.muted }}>
                    {i.reason}
                    {i.restoredAt && (
                      <div style={{ color: "#856404", marginTop: ".2rem" }}>
                        ↩ Restituido el {fecha(i.restoredAt)} por {i.restoredBy}
                        {i.restoreReason ? `: ${i.restoreReason}` : ""}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, whiteSpace: "nowrap" }}>
                    {i.restoredAt ? (
                      <span style={{ color: COLORS.muted, fontSize: ".8rem" }}>Restituido</span>
                    ) : i.stillWrittenOff.length === 0 ? (
                      <span
                        style={{ color: COLORS.muted, fontSize: ".8rem" }}
                        title="El ledger ya no tiene recibos condonados vigentes para este cliente"
                      >
                        Sin saldo condonado
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setRestoring(i);
                          setMotivo("");
                        }}
                        style={{
                          border: `1px solid ${COLORS.link}`,
                          color: COLORS.link,
                          background: "transparent",
                          borderRadius: ".4rem",
                          padding: ".2rem .6rem",
                          cursor: "pointer",
                          fontSize: ".8rem",
                        }}
                      >
                        Restituir…
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {restoring && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: ".6rem",
              padding: "1.2rem 1.4rem",
              maxWidth: 480,
              width: "92%",
              boxShadow: "0 8px 30px rgba(0,0,0,.2)",
            }}
          >
            <h3 style={{ margin: "0 0 .4rem", color: COLORS.heading, fontSize: "1.05rem" }}>
              Restituir {eur(restoring.centsWrittenOff)} € condonados
            </h3>
            <p style={{ color: COLORS.muted, fontSize: ".85rem", margin: "0 0 .7rem" }}>
              La deuda vuelve a ser exigible y el cliente reaparece en la cartera de
              impagados (puedes volver a meterlo en cadencia desde allí). La condonación
              original <strong>no se borra</strong>: en la historia del recibo quedan los dos
              hechos, el de {restoring.actor} y el tuyo.
            </p>
            <label style={{ display: "grid", gap: ".25rem", marginBottom: ".8rem" }}>
              <span style={{ fontSize: ".78rem", color: COLORS.muted }}>
                Motivo de la corrección (obligatorio)
              </span>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                placeholder="p.ej. condonado por error, el cliente sí es reclamable"
                style={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: ".4rem",
                  padding: ".45rem .6rem",
                  fontSize: ".85rem",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
              />
            </label>
            <div style={{ display: "flex", gap: ".5rem", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setRestoring(null)}
                disabled={busy}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  background: "transparent",
                  color: COLORS.heading,
                  borderRadius: ".4rem",
                  padding: ".35rem .8rem",
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void doRestore()}
                disabled={motivo.trim().length < 5 || busy}
                style={{
                  border: `1px solid ${COLORS.link}`,
                  background: motivo.trim().length >= 5 ? COLORS.link : "#9dc0f5",
                  color: "#fff",
                  borderRadius: ".4rem",
                  padding: ".35rem .8rem",
                  cursor: motivo.trim().length >= 5 ? "pointer" : "not-allowed",
                }}
              >
                {busy ? "Restituyendo…" : "Restituir la deuda"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
